import React, { useState } from "react";
import "./OurStory.css"; // for the background image fade (optional but recommended)
import { Helmet } from "react-helmet";

const SLIDES = [
  {
    year: 2012,
    title: "Laying the Foundation for Reliability.",
    description:
      "Our journey began in 2012 with a clear focus on stability and seamless power management. We recognized that many industries and households were struggling with manual power transitions that led to equipment damage and productivity loss. To solve this, we launched our Automatic Transfer Switch (ATS) line. This was our first step in providing the critical ability to switch power sources—from mains to backup—without human intervention. This phase set the standard for the technical excellence and safety-first mindset that defines SEA today, proving that automation could be both simple and life-changing.",
    bgImage: "https://images.unsplash.com/photo-1523217582562-09d0def993a6?auto=format&fit=crop&w=1400&q=80", // replace with your 1968 image
  },
  {
    year: 2014,
    title: "Embracing the Connected World.",
    description:
      "As the world entered a new era of data, we recognized that the future of automation lay in connectivity. In 2014, SEA took a bold leap by entering the field of IoT (Internet of Things) Development. We understood that hardware alone was no longer sufficient for a modernizing world; our clients needed thinking machines. This move allowed us to begin integrating smart sensors and communication modules into our hardware, laying the groundwork for systems that could communicate, learn, and optimize themselves. We transitioned from being a component manufacturer to a smart-systems architect, ensuring our customers were ready for the digital revolution.",
    bgImage:
      "https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=1400&q=80",
  },
  {
    year: 2017,
    title: "Empowering Sustainable Agriculture.",
    description:
      "With a deep commitment to green energy and a desire to support the backbone of our economy—the agricultural sector—we ventured into renewable energy in 2017. We saw the struggles of farmers dealing with inconsistent power grids and the rising costs of diesel. By starting the manufacturing of Solar Pumps, we provided a sustainable, cost-effective way to manage water resources using the power of the sun. This wasn't just a business expansion; it was a mission to bring independence to remote locations and promote eco-friendly farming practices, ensuring that power was available even where the grid could not reach.",
    bgImage:
      "https://images.unsplash.com/photo-1502672023488-70e25813eb80?auto=format&fit=crop&w=1400&q=80",
  },
  {
    year: 2019,
    title: "Mastering Solar Control and Efficiency.",
    description:
      "Engineering a pump was only the beginning; the true challenge lay in maximizing its efficiency under varying environmental conditions. In 2019, we expanded our manufacturing capabilities to include high-performance Solar Water Pump Controllers. By designing and building the brains behind the pump in-house, we were able to offer more precise motor control, enhanced durability, and superior energy harvesting. Our controllers were engineered to survive harsh climates, ensuring that every watt of solar energy was converted into water flow, providing our users with a level of reliability that traditional systems simply couldn't match..",
    bgImage:
      "https://images.unsplash.com/photo-1460317442991-0ec209397118?auto=format&fit=crop&w=1400&q=80",
  },
  {
    year: 2024,
    title: "The Digital Leap into Global Monitoring.",
    description: `The year 2024 marks our most significant technological milestone to date. Recognizing that modern users need transparency and data at their fingertips, we launched our comprehensive Remote Monitoring System (RMS). 

We didn't stop at the hardware; we built a complete digital ecosystem. This included a dedicated web platform and a feature-rich mobile application accessible at rms.seaiot.in.

Our RMS allows clients to monitor health parameters, track energy output, and manage their assets in real-time from anywhere in the world.

By bringing total transparency and remote diagnostics to our solar and automation products, we have reduced downtime and maintenance costs for our users.

SEA is now leading the charge in the Indian IoT and Automation landscape, providing a safety net of data and control for every client.

**Looking Ahead**
As we move forward, SEA continues to innovate at the intersection of power, water, and data. Our journey from a local automation specialist to a high-tech IoT and Solar manufacturing hub is a testament to our resilience and our customers' trust.

We remain dedicated to engineering a smarter, greener, and more connected world for everyone, ensuring that whenever a gap in technology exists, SEA is there to fill it.`,
    bgImage: "https://images.unsplash.com/photo-1502672023488-70e25813eb80?auto=format&fit=crop&w=1400&q=80",
  },
];

const OurStory = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const currentSlide = SLIDES[currentIndex];

  const handleYearClick = (index) => {
    setCurrentIndex(index);
  };

  const handlePrev = () => {
    setCurrentIndex((prev) =>
      prev === 0 ? SLIDES.length - 1 : prev - 1
    );
  };

  const handleNext = () => {
    setCurrentIndex((prev) =>
      prev === SLIDES.length - 1 ? 0 : prev + 1
    );
  };

  return (
    <>
      <Helmet>
        <title>Our Story | The Journey of Sandip electronics  & Automation</title>
        <meta name="description" content="Discover the journey of SEA from a 2013 engineering startup in  Pune(India)e to a pioneer in IoT-enabled solar technology. Founded by Mr. Sandip Kute with a vision for smart automation." />
        <meta name="keywords" content="Sandip electronics  story, history of SEA Pune, Sandip Kute founder, solar technology innovation journey, Indian electronics  startup success" />
        <meta property="og:title" content="From Vision to Reality: The Story of SEA" />
        <meta property="og:description" content="Read how we combined electronics  engineering with a passion for agriculture to create SEA's IoT solar solutions." />
        <meta property="og:image" content="https://official.seaiot.in/assets/story-milestones-banner.jpg" />
        <meta property="og:url" content="https://official.seaiot.in/OurStory" />
        <link rel="canonical" href="https://official.seaiot.in/OurStory" />
      </Helmet>
      <div className="our-story-page">
        {/* Background image that changes by year */}
        <div
          className="our-story-bg"
          style={{ backgroundImage: `url(${currentSlide.bgImage})` }}
        />

        {/* Glass card */}
        <div className="our-story-card">
          {/* Top text */}
          <div className="our-story-header">
            <span className="our-story-label">HISTORY</span>
            <h1 className="our-story-title">Our Story</h1>
            <p className="our-story-subtitle">
              At Sandip Electronics and Automation (SEA), our story is one of constant evolution and a commitment to filling the gaps in the technological landscape. Much like the most essential service providers in the world, SEA was established to provide advanced electronics and automation solutions when standard market offerings fell short of the reliability and innovation our customers deserved. We don’t just build products; we engineer intelligent systems that bridge the gap between traditional power infrastructure and the digital future.
            </p>
          </div>

          {/* Years row */}
          <div className="our-story-years">
            {SLIDES.map((slide, index) => (
              <button
                key={slide.year}
                className={"year-btn" + (index === currentIndex ? " active" : "")}
                onClick={() => handleYearClick(index)}
              >
                {slide.year}
              </button>
            ))}
          </div>

          {/* Content card floating over houses */}
          <div className="our-story-content-wrapper">
            <div className="our-story-content-card">
              <p className="our-story-text">
                {currentSlide.title}
                <br />
                <br />
                {currentSlide.description}
              </p>

              <div className="our-story-nav">
                <button className="nav-btn" onClick={handlePrev}>
                  «
                </button>
                <button className="nav-btn" onClick={handleNext}>
                  »
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>

  );
};

export default OurStory;
